// src/prototype/hooks/useEntitlements.ts
// ============================================================
// ENTITLEMENTS — single gating source for the EA (storefront) app.
//
// Wraps the demo plan (a localStorage override on the real entitlement) and
// exposes SEMANTIC capabilities so gates never scatter `isPro` / `plan === 'pro'`
// checks across the codebase. Every gate consumes this hook.
//
// Tiers:
//   FREEMIUM — everything under Personalization (incl. Advanced Personalization
//     / weight settings), the Portfolio Strategy tab of Evaluate, and the
//     top-3 ranked use cases (Use Case Profile + Scoring Rationale).
//   PRO — everything else: the Use Case Explorer beyond the top 3, all ROI
//     models, portfolio finalization, and the entire Execute section
//     (playbooks, Command Centers, PDF artifacts).
//
// The demo upgrade flip still lives in useDemoPlan (EAUpgradePage.upgrade()).
// ============================================================

import { useMemo } from 'react';
import { useDemoPlan } from '../../hooks/useDemoPlan';

/** Freemium sees the full ranked list but may only open the top N use cases. */
export const FREEMIUM_EXPLORER_LIMIT = 3;

export interface EntitlementCapabilities {
  /** Portfolio Strategy tab of Evaluate (freemium+). */
  viewPortfolioIntelligence: boolean;
  /** Advanced Personalization / weight settings (freemium+). */
  adjustWeights: boolean;
  /** Full ranked list is always visible (rows beyond top-N are locked). */
  viewRankedList: boolean;
  /** Open Explorer rows beyond the freemium top-N (pro). */
  exploreBeyondTopN: boolean;
  /** Full ROI model (pro — even for the top-3 use cases). */
  viewRoiModel: boolean;
  /** Add to / finalize the portfolio (pro). */
  finalizePortfolio: boolean;
  /** Execute section — playbooks, Command Centers, PDF artifacts (pro). */
  execute: boolean;
}

export interface Entitlements {
  plan: 'freemium' | 'pro';
  isPro: boolean;
  isFreemium: boolean;
  isLoading: boolean;
  /** Demo flip to Pro (proxies useDemoPlan.upgrade). */
  upgrade: () => void;
  freemiumExplorerLimit: number;
  can: EntitlementCapabilities;
  /**
   * Whether the current plan may open a use case's Profile / Scoring Rationale.
   * Pro: always. Freemium: only within the top-N ranked use cases.
   */
  canViewOpportunityDetail: (rank?: number) => boolean;
}

export function useEntitlements(): Entitlements {
  const { plan, isPro, isFreemium, isLoading, upgrade } = useDemoPlan();

  const can = useMemo<EntitlementCapabilities>(
    () => ({
      viewPortfolioIntelligence: true,
      adjustWeights: true,
      viewRankedList: true,
      exploreBeyondTopN: isPro,
      viewRoiModel: isPro,
      finalizePortfolio: isPro,
      execute: isPro,
    }),
    [isPro],
  );

  const canViewOpportunityDetail = (rank?: number): boolean =>
    isPro || (typeof rank === 'number' && rank > 0 && rank <= FREEMIUM_EXPLORER_LIMIT);

  return {
    plan,
    isPro,
    isFreemium,
    isLoading,
    upgrade,
    freemiumExplorerLimit: FREEMIUM_EXPLORER_LIMIT,
    can,
    canViewOpportunityDetail,
  };
}

export default useEntitlements;
